// Parent Progress tab: Learning Adventures — Learn-Loop mastery across
// Science, Social Studies, Computing, Life Skills and Reading & Writing.
// Consumes the same /api/learn/{child}/mastery records the child shell writes.
import { useEffect, useState } from "react";
import { Compass } from "lucide-react";
import { api } from "../../lib/api";

const SUBJECT_LABELS = {
  science: "Science",
  social: "Social Studies",
  computing: "Computing & AI",
  life: "Life Skills",
  reading: "Reading & Writing",
};
const LEVEL_ORDER = ["mastered", "proficient", "practicing", "learning"];
const LEVEL_TONES = {
  mastered: "bg-emerald-100 text-emerald-900",
  proficient: "bg-sky-100 text-sky-900",
  practicing: "bg-amber-100 text-amber-900",
  learning: "bg-muted text-muted-foreground",
};

const summarize = (rows) => {
  const by = {};
  rows.forEach((r) => {
    const s = (by[r.subject] ||= { total: 0 });
    s.total += 1;
    s[r.level] = (s[r.level] || 0) + 1;
  });
  return by;
};

export const LearnLoopMastery = () => {
  const [kids, setKids] = useState(null);

  useEffect(() => {
    api.get("/children").then(async ({ data }) => {
      const out = [];
      for (const c of data) {
        try {
          const { data: m } = await api.get(`/learn/${c.child_id}/mastery`);
          out.push({ child: c, subjects: summarize(m.mastery || []) });
        } catch { /* child without consent/progress stays hidden */ }
      }
      setKids(out);
    }).catch(() => setKids([]));
  }, []);

  if (kids === null) {
    return (
      <section className="bg-card rounded-2xl border border-border p-5 animate-pulse h-24"
        data-testid="learnloop-mastery-loading" />
    );
  }
  const active = kids.filter((k) => Object.keys(k.subjects).length);
  if (!active.length) return null; // nothing started yet — stay quiet

  return (
    <section className="bg-card rounded-2xl border border-border p-5 space-y-4"
      data-testid="learnloop-mastery-card">
      <div className="flex items-center gap-2">
        <Compass className="h-5 w-5 text-primary" aria-hidden="true" />
        <h2 className="text-lg font-extrabold font-display">Learning Adventures</h2>
      </div>
      <p className="text-xs text-muted-foreground">
        Lesson mastery from the shared Learn Loop — Science, Social Studies,
        Computing &amp; AI, Life Skills and Reading &amp; Writing.
      </p>
      {active.map(({ child, subjects }) => (
        <div key={child.child_id} className="space-y-2"
          data-testid={`learnloop-mastery-${child.child_id}`}>
          <p className="text-sm font-extrabold">{child.display_name}</p>
          <ul className="space-y-1.5">
            {Object.entries(subjects).map(([subject, s]) => (
              <li key={subject}
                className="flex items-center gap-2 flex-wrap text-xs rounded-lg bg-secondary/40 px-2.5 py-1.5"
                data-testid={`learnloop-subject-${child.child_id}-${subject}`}>
                <span className="font-bold min-w-32">
                  {SUBJECT_LABELS[subject] || subject}
                </span>
                {LEVEL_ORDER.filter((lv) => s[lv]).map((lv) => (
                  <span key={lv}
                    className={`rounded-full px-2 py-0.5 font-bold ${LEVEL_TONES[lv]}`}>
                    {s[lv]} {lv}
                  </span>
                ))}
              </li>
            ))}
          </ul>
        </div>
      ))}
    </section>
  );
};
